// @flow

import * as React from 'react';

import debounce from 'lodash/debounce';

import toPairs from 'lodash/toPairs';

import { useTranslator } from '@gandi/translate';
import {
  Base,
  InputNumber,
  InputNumberInput,
  InputNumberStepDown,
  InputNumberStepUp,
  Text,
} from '@gandi/ui';

import styles from './Counter.scss';

export type Props = {
  id: string,
  min?: number,
  max?: number,
  count: number,
  isLoading: boolean,
  /**
   * An array of dependencies that will be passed to the input change debounce function.
   */
  callbackDependencies?: Array<mixed>,
  onChange: (value: number) => mixed,
};

type SetActive = (id: string, status: boolean) => void;

/**
 * Use this context to track debouncing on Counter.
 * This requires `CounterStateProvider` to work.
 */
const CounterStateContext: React.Context<{
  hasActivity: boolean,
  setActive: SetActive,
} | null> = React.createContext(null);

type CounterState = {
  [id: string]: boolean,
};
/**
 * [optional] Allow to track the activity of one or more Counter. e.g.
 *
 * const TrackCounter = () => {
 *   const { hasActivity } = useContext(CounterStateContext)
 *
 *   return hasActivity ? 'debouncing' : '−';
 * }
 *
 * <CounterStateProvider>
 *   <TrackCounter />
 *   <Counter id="a" />
 *   <Counter id="b" />
 * </CounterStateProvider>
 */
export const CounterStateProvider = ({
  children,
}: {
  children: React.Node,
}): React.MixedElement => {
  const [details, dispatch] = React.useReducer<
    CounterState,
    { id: string, status: boolean },
  >((state, action) => {
    if (state[action.id] === action.status) {
      return state;
    }

    return {
      ...state,
      [action.id]: action.status,
    };
  }, {});

  const setActive = (id: string, status: boolean) => {
    dispatch({ id, status });
  };

  // find at least one active counter
  const hasActivity = !!toPairs(details).some(item => !!item[1]);

  const value = React.useMemo(
    () => ({
      setActive,
      hasActivity,
    }),
    [hasActivity],
  );

  return (
    <CounterStateContext.Provider value={value}>
      {children}
    </CounterStateContext.Provider>
  );
};

/**
 * Input number for Product's quantity.
 *
 * Optionnaly CounterStateProvider & CounterStateContext can be used to track debounce activity
 */
export default function Counter({
  id,
  min = 1,
  max,
  count,
  isLoading,
  onChange,
  callbackDependencies = [],
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  // value that is shown in the input, it can be different from count when the user is typing a new value that is not yet validated
  const [value, setValue] = React.useState<number>(count);
  // last valid value that was passed to the onChange callback, it is used to reset the input value when the user enters an invalid value
  const [lastValidValue, setLastValidValue] = React.useState<number>(count);
  // set the current activity of the counter in a context that can be used in a parent component
  const counterState = React.useContext(CounterStateContext);

  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const step = 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChange = React.useCallback(
    debounce((val: number) => {
      if (counterState) {
        // no more activity
        counterState.setActive(id, false);
      }
      return onChange(val);
    }, 2000),
    [...callbackDependencies],
  );

  const handleBlur = () => {
    // show the last valid value when the user leaves the input with an invalid value
    setValue(lastValidValue);
    // empty the error message when the user leaves the input, it will be set again if the value is still invalid
    setErrorMessage('');
  };

  const handleChange = (evt: SyntheticInputEvent<>) => {
    const newValue = evt.target.valueAsNumber;

    // display the user value
    setValue(newValue);

    // stop debounced change to be applied while the user is changing the value,
    // it will be restarted at the end of the function if the value is valid
    debouncedInputChange.cancel();

    // validation
    if (newValue < min) {
      setErrorMessage(__('Error: minimum %(min)s', { min }));
      return;
    }
    if (Number.isFinite(max) && newValue > max) {
      setErrorMessage(__('Error: maximum %(max)s', { max }));
      return;
    }

    // reset error message
    setErrorMessage('');

    // do not patch if not needed
    if (newValue === lastValidValue) return;

    setLastValidValue(newValue);
    if (counterState) {
      // indicate that counter is debouncing
      counterState.setActive(id, true);
    }

    // patch the cart
    debouncedInputChange(newValue);
  };

  const handleValueChange = (newValue: number) => {
    if (Number.isFinite(newValue) && newValue >= min) {
      setValue(newValue);
      if (counterState) {
        // indicate that counter is debouncing
        counterState.setActive(id, true);
      }
      debouncedInputChange(newValue);
    }
  };

  React.useEffect(() => {
    setValue(count);
    return function cleanup() {
      debouncedInputChange.cancel();
    };
  }, [count, debouncedInputChange]);

  // TODO: migrate to https://www.reshaped.so/docs/components/number-field
  return (
    <Base>
      <InputNumber small>
        <InputNumberStepDown
          disabled={value <= min || isLoading}
          onClick={() => handleValueChange(value - step)}
        >
          {__('Less')}
        </InputNumberStepDown>
        {/* $FlowFixMe[prop-missing] onBlur is missing but works anyway as <InputNumberInput> spreads all passed props */}
        <InputNumberInput
          id={id}
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={isLoading}
          style={{
            maxWidth: 'calc(40px + 5ch)',
            textAlign: 'center',
          }}
          onChange={handleChange}
          onBlur={handleBlur}
          className={errorMessage ? styles.error : undefined}
        />
        <InputNumberStepUp
          disabled={(max ? value >= max : false) || isLoading}
          onClick={() => handleValueChange(value + step)}
        >
          {__('More')}
        </InputNumberStepUp>
        {errorMessage !== '' && <Text color="danger">{errorMessage}</Text>}
      </InputNumber>
    </Base>
  );
}
